'use client';

import { StockStatus } from '@eliadmin/core/src/components/dlz/ui/badge';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatDeliveryDateRange } from '@eliadmin/core/src/utils/delivery-date-utils';
import { normalizeIntlLocale } from '@eliadmin/core/src/utils/intl-utils';
import { getProductQuantityRules } from '@eliadmin/core/src/utils/product-utils';

/**
 * Figma „Stock row“: availability of the selected variant (kit `StockStatus`), the delivery window
 * when the store shows it, and the dispatch note of the administration for goods in stock.
 * Same availability rules as the core `ProductStock`.
 */
export function StockRow({ dispatchNote }: { dispatchNote: string }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const settings = useGlobalStore(state => state.templateSettings.product_detail);
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const item = selectedVariant ?? product;
  const availability = selectedVariant?.availability ?? product.availability;
  const unavailability = selectedVariant?.unavailability ?? product.unavailability;
  const inStock = item.quantity >= product.min_quantity;
  const available = inStock || !!availability?.sellable;
  const status = available ? availability : unavailability;

  const tone = inStock ? 'in-stock' : available ? 'on-order' : 'out-of-stock';
  const name = status?.name ?? t(available ? 'text238' : 'text495');
  // Stock of pack products (`custom_quantity`) is counted in packs, not in the unit of the pack content.
  const soldInPacks =
    Number(getProductQuantityRules(product, selectedVariant)?.custom_quantity) > 1;
  const count =
    inStock && item.quantity > 0
      ? ` ${item.quantity.toLocaleString(normalizeIntlLocale(locale))} ${
          (!soldInPacks && product.unit) || t('text248')
        }`
      : '';
  const days = status?.availability_in_days;
  const delivery =
    settings?.show_availability_delivery_days?.enabled && days
      ? `${t('text3497')} ${formatDeliveryDateRange({
          fromDays: days,
          toDays: days + 2,
          locale,
        })}`
      : '';
  const note = tone === 'in-stock' ? dispatchNote : '';
  const details = [delivery, note].filter(Boolean);

  if (!settings?.show_stock?.enabled && details.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
      {settings?.show_stock?.enabled ? (
        <StockStatus tone={tone}>
          {name}
          {count}
        </StockStatus>
      ) : null}
      {details.map((detail, index) => (
        <span key={detail} className="contents">
          {index > 0 || settings?.show_stock?.enabled ? (
            <span aria-hidden="true" className="hidden size-1 rounded-full bg-[#E2E4E7] sm:block" />
          ) : null}
          <span className="dlz-caption sm:dlz-body-s text-[#4F545C]">{detail}</span>
        </span>
      ))}
    </div>
  );
}
